import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography,
    TextField, Button, Select, MenuItem, InputAdornment, IconButton, Divider, Alert, Box, Switch
} from '@mui/material';
import { toPascalCase, validateEmail, digestMessage, removeAllSpaces } from "common_utils/utils";
import PasswordStrengthBar from 'react-password-strength-bar';
import Snackbar from '@mui/material/Snackbar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import colors from "assets/mavric-theme/base/colors.js";
import typography from "assets/mavric-theme/base/typography";
import borders from "assets/mavric-theme/base/borders";
import { addNewAdminPortalUser } from 'apis/CustomersApis';
const { borderRadius } = borders;
const { primary, secondary, text, transparent, white } = colors;
const { size } = typography;

const AddUserDialog = ({ open, onClose, userRoleList, }) => {

    const initialUserInfo = { user_id: 0, role: "", email: "", password: "", first_name: "", last_name: "" };
    const [userInfo, setUserInfo] = useState(initialUserInfo);
    const [firstName, setFirstName] = useState(userInfo.first_name);
    const [lastName, setLastName] = useState(userInfo.last_name);
    const [email, setEmail] = useState(userInfo.email);
    const [role, setRole] = useState(userRoleList.length > 0 ? userRoleList[0].role : "");
    const [password, setPassword] = useState(userInfo.password);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [viewAllDemoAccounts, setViewAllDemoAccounts] = useState(false);
    const [demoAccountsModified, setDemoAccountsModified] = useState(false);

    const [fieldErrors, setFieldErrors] = useState({
        firstName: { error: false, message: '' },
        lastName: { error: false, message: '' },
        email: { error: false, message: '' },
        role: { error: false, message: '' },
        password: { error: false, message: '' }
    });
    const [pageAlertType, setPageAlertType] = useState("error");
    const [pageAlertMsg, setPageAlertMsg] = useState("");
    // const [showAlert, setShowAlert] = useState(alertState.open);

    const [alertState, setAlertState] = useState({ open: false, message: '', severity: 'success', vertical: 'bottom', horizontal: 'left' });
    const { vertical, horizontal } = alertState;
    const showAlert = (message, severity = 'success') => {
        setAlertState(prev => ({ ...prev, open: true, message, severity }));
    };
    const resetForm = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setRole('');
        setPassword('');
        setError(null);
        setViewAllDemoAccounts(false);
        setDemoAccountsModified(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleConfirmClose = () => {
        setConfirmOpen(false);
        handleClose();
    };

    const validateForm = () => {
        setError(null);
        const errors = {
            firstName: { error: false, message: '' },
            lastName: { error: false, message: '' },
            email: { error: false, message: '' },
            role: { error: false, message: '' },
            password: { error: false, message: '' }
        };

        if (!firstName || !firstName.trim()) {
            setError("First name is required");
            errors.firstName = { error: true, message: 'First name is required' };
            return false;
        }

        if (!email || !email.trim()) {
            setError("Email is required");
            errors.email = { error: true, message: 'Email is required' };
            return false;
        } else if (!validateEmail(email)) {
            setError("Please enter a valid email address");
            errors.email = { error: true, message: 'Invalid email format' };
            return false;
        }


        if (!role) {
            setError("Role selection is required");
            errors.role = { error: true, message: 'Role is required' };
            return false;
        }


        if (!password && !password.trim()) {
            setError("Password is required");
            errors.password = { error: true, message: 'Password is required' };
            return false;
        } else if (password.length < 3) {
            setError("Password must be at least 3 characters");
            errors.password = { error: true, message: 'Must be at least 3 characters' };
            return false;
        } else if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
            setError("Password must contain both letters and numbers");
            return false;
        }
        setFieldErrors(errors)
        //all validations pass
        return true;

    };


    const handleSubmit = async () => {

        if (!validateForm()) return;
        setIsSubmitting(true);
        const newUserInfo = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
            role: role,
            password: password,
        };

        if (role === "salesoperator") {
            newUserInfo.enable_view_all_demo_accounts = viewAllDemoAccounts;
            newUserInfo.set_view_all_demo_accounts_flag = true;
        }

        digestMessage(password).then(newHashPswd => {
            newUserInfo.password = newHashPswd;
            // console.log("new user Info", newUserInfo);

            addNewAdminPortalUser(newUserInfo).then(result => {
                // console.log("User created:", result);
                if (result.user_id) {
                    setConfirmMessage('User created successfully');
                    showAlert('User created successfully', 'success');
                    setConfirmOpen(true);
                    // resetForm();
                } else {
                    setError("User creation failed");
                    showAlert('User creation failed', 'error');
                }
            }).catch((error) => {
                setError(error.response.data.message || "Failed to create user");
            }).finally(() => {
                setIsSubmitting(false);
            })
        })


    };

    const handleAlertClose = (event, reason) => {

        if (reason === 'clickaway') {
            return;
        }
        setAlertState({ ...alertState, open: false });
        // setShowAlert(false);

    }


    return (
        <div>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                sx={{ '& .MuiDialog-paper': { backgroundColor: primary.main, } }}
            >
                <DialogTitle>
                    <Typography
                        variant="body2"
                        color={secondary.main}
                        sx={{ fontWeight: 600, mb: 1 }}
                        gutterBottom
                    >
                        Add New User
                    </Typography>
                </DialogTitle>

                <DialogContent>

                    <Grid container spacing={2} direction="row" sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4} md={4} lg={4}>
                            <Typography
                                error={fieldErrors.firstName.error}
                                helperText={fieldErrors.firstName.error ? fieldErrors.firstName.message : ''}
                                variant="body2"
                                fontSize={size.sm}
                                color={text.main}
                                sx={{ fontWeight: 400, mb: 1 }}
                            >
                                First Name*
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="First Name"
                                InputProps={{
                                    sx: {
                                        color: text.main,
                                        backgroundColor: transparent.main,
                                        borderRadius: borderRadius.md
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={4}>
                            <Typography
                                variant="body2"
                                fontSize={size.sm}
                                color={text.main}
                                sx={{ fontWeight: 400, mb: 1 }}
                            >
                                Last Name
                            </Typography>
                            <TextField
                                fullWidth
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Last Name"
                                InputProps={{
                                    sx: {
                                        color: text.main,
                                        backgroundColor: transparent.main,
                                        borderRadius: borderRadius.md
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={4}>
                            <Typography
                                variant="body2"
                                fontSize={size.sm}
                                color={text.main}
                                sx={{ fontWeight: 400, mb: 1 }}
                            >
                                Email Address*
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email Address"
                                InputProps={{
                                    sx: {
                                        color: text.main,
                                        backgroundColor: transparent.main,
                                        borderRadius: borderRadius.md
                                    }
                                }}
                            />
                            <Typography
                                variant="body2"
                                fontSize={size.xs}
                                color={secondary.main}
                                sx={{ fontWeight: 400, mt: 1 }}
                            >
                                Note: This will be the user's Login ID
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={4}>
                            <Typography
                                variant="body2"
                                fontSize={size.sm}
                                color={text.main}
                                sx={{ fontWeight: 400, mb: 1 }}
                            >
                                Role Type*
                            </Typography>
                            <Select
                                fullWidth
                                name="usrRole"
                                id="usrRole"
                                value={role || ""}
                                onChange={(e) => setRole(e.target.value)}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <em style={{ fontStyle:'normal', color: '#aaa' }}>Select Role</em>;
                                    }
                                    return selected;
                                }}
                                sx={{
                                    '.MuiSelect-select': {
                                        paddingTop: '8px !important',
                                        paddingBottom: '8px !important',
                                        minHeight: '35px !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '1em !important'
                                    }
                                }}
                            >
                                {/* <MenuItem value="" disabled>
                                    Select Role
                                </MenuItem> */}
                                {userRoleList.map((role) => (
                                    <MenuItem key={role.id} value={role.role}>
                                        {role.role}
                                    </MenuItem>
                                ))}

                            </Select>
                        </Grid>

                        <Grid item xs={12} sm={4} md={4} lg={4}>
                            <Typography
                                variant="body2"
                                fontSize={size.sm}
                                color={text.main}
                                sx={{ fontWeight: 400, mb: 1 }}
                            >
                                Password*
                            </Typography>
                            <TextField
                                required
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                InputProps={{
                                    sx: {
                                        color: text.main,
                                        backgroundColor: transparent.main,
                                        borderRadius: borderRadius.md
                                    },
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOffIcon color="secondary" /> : <VisibilityIcon color="secondary" />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <PasswordStrengthBar password={password} />
                        </Grid>

                        {(role === "salesoperator") && (
                            <Grid item xs={12} sm={4} md={4} lg={4} sx={{ pt:5 }}>
                                <Grid item >
                                    <Typography
                                        variant="body2"
                                        fontSize={size.sm}
                                        justifyContent="center"
                                        color={text.main}
                                        sx={{ fontWeight: 400, mb: 0 }}>
                                        View All Demo Accounts
                                    </Typography>
                                </Grid>
                              
                                <Switch
                                    checked={viewAllDemoAccounts}
                                    onChange={(e) => {
                                        setViewAllDemoAccounts(e.target.checked);
                                        setDemoAccountsModified(true); 
                                    }}
                                    color="primary"
                                    inputProps={{ 'aria-label': 'controlled' }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: 'auto', maxWidth: '100%', pr: 2, }}>
                    {(error) && (
                        <Alert variant="filled" severity="error" onClick={() => setError(undefined)} sx={{ py: 0 }}>
                            <Typography fontSize={size.sm} color={white.main} sx={{ fontWeight: 400 }}>{error}</Typography>
                        </Alert>
                    )}
                </Box>

                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleClose} color="secondary" variant="contained" sx={{ borderRadius: "4px", padding: "10px 20px", }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        color="secondary"
                        sx={{ borderRadius: "4px", padding: "10px 20px", }}
                    >
                        {isSubmitting ? 'Creating...' : 'Save'}
                    </Button>
                </DialogActions>


            </Dialog>

            {/* Success confirmation dialog */}
            <Dialog
                open={confirmOpen}
                onClose={handleConfirmClose}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: primary.main,
                    }
                }}
            >
                <DialogTitle>
                    <Typography fontSize={size.lg} color={text.main} sx={{ fontWeight: 600 }}>
                        Success
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography>{confirmMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={handleConfirmClose}
                        color="secondary"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                open={alertState.open}
                autoHideDuration={5000}
                onClose={handleAlertClose}
                anchorOrigin={{ vertical, horizontal }}
                key={`snackbar-${alertState.message}`}
            >
                <Alert
                    onClose={handleAlertClose}
                    severity={alertState.severity}
                    variant='filled'
                    elevation={6}
                    sx={{
                        '& .MuiAlert-message': { color: 'white.main', fontSize: size.sm },
                        marginBottom: 4
                    }}
                >
                    {alertState.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default AddUserDialog
